import { applyMentionConsumptionToMemory } from '../utils/mentionRuleRecovery';
import { assertOriginalRuleConfiguration } from '../utils/originalRuleConfiguration';
/**
 * MentionRuleService - Handles mention-based rule processing
 *
 * Mention rules trigger consequences when a student accumulates a certain
 * number of mentions within a specified timeframe and scope.
 */

import { isPhaseExecutionError, phaseExecutionError, phaseRuleAllowsRecord } from '../../domain/phaseRuntime.js';
import { ruleRecordInRange } from '../../domain/schoolRulePeriods.js';
import { isSchoolRulePeriodError } from '../utils/dateHelpers';

import {
  collection,
  query,
  where,
  getDocs,
  getDocFromServer,
  doc,
  updateDoc,
  Timestamp,
  arrayUnion
} from 'firebase/firestore';
import { db, functions, httpsCallable } from '../../../firebase';
import { getDateRangeForTimeframe } from '../utils/dateHelpers';
import { disciplineLogger } from '../../../utils/disciplineLogger';
import { countReads } from '../../../utils/firebaseReadCounter';
import { processingGuard } from '../utils/ProcessingGuard';

const RULE_TIME_ZONE = 'Africa/Johannesburg';

// Use incident time for rule windows. Capture time is audit metadata and must
// never make delayed records appear to have happened together.
export const getMentionRuleDate = mention => {
  const value = mention?.issuedAt || mention?.createdAt;
  return value?.toDate?.() || new Date(value);
};

export class MentionRuleService {
  constructor(dependencies) {
    this.ruleService = dependencies.ruleService;
    this.mentionService = dependencies.mentionService;
    this.consequenceService = dependencies.consequenceService;
    this.usesTrustedMentions = !this.ruleService?.collectionName || this.ruleService.collectionName === 'disciplineRules';
    this.contractService = dependencies.contractService || null; // Optional
  }

  /**
   * Process mention rules for a mention
   * @param {Object} mention - The mention to process
   * @param {Object} contract - Optional active contract
   * @param {Object} studentData - Pre-fetched student data
   * @returns {Promise<Array>} Consequences created by matching rules
   */
  async processMentionRules(mention, contract = null, studentData) {
    try {
      const createdConsequences = [];
      // Get all active mention rules
      const activeRules = await this.ruleService.getActiveRules(mention.schoolId);
      const phasePolicy = await this.ruleService.getPhasePolicy?.(mention.schoolId);
      assertOriginalRuleConfiguration(mention.ruleConfiguration, activeRules, phasePolicy);
      const mentionRules = activeRules
        .filter(r => r.type === 'mention')
        .sort((a, b) => {
          // Time scope: day < week < month < term (shorter is more specific)
          const timeOrder = {'day': 1, 'week': 2, 'month': 3, 'term': 4};
          const timeDiff = (timeOrder[a.timeframe] || 5) - (timeOrder[b.timeframe] || 5);
          if (timeDiff !== 0) return timeDiff;

          // Mention scope specificity
          const scopeOrder = {'same-offense': 1, 'same-lesson': 2, 'same-category': 3, 'any': 4};
          const scopeDiff = (scopeOrder[a.mentionScope] || 5) - (scopeOrder[b.mentionScope] || 5);
          if (scopeDiff !== 0) return scopeDiff;

          return (a.sortOrder || 999) - (b.sortOrder || 999);
        });

      disciplineLogger.info(`Evaluating ${mentionRules.length} mention rules`);

      for (const rule of mentionRules) {
        const consequence = await this.evaluateMentionRule(rule, mention, contract, studentData, phasePolicy);
        if (consequence) createdConsequences.push(consequence);
      }
      return createdConsequences;
    } catch (error) {
      if (error.ruleConfigurationChanged || isSchoolRulePeriodError(error) || isPhaseExecutionError(error) ||
          error.thresholdProcessingFailed) throw error;
      console.error('Error processing mention rules:', error);
      return [];
    }
  }

  async recoverSavedResult(mention, ruleId) {
    const {data} = await httpsCallable(functions, 'readBehaviourMentionRuleResult')({
      schoolId: mention.schoolId, studentId: mention.studentId, mentionId: mention.id, ruleId,
    });
    if (!data.actionId) return null;
    const snapshot = await getDocFromServer(doc(db, 'schools', mention.schoolId, 'disciplineQueue', data.actionId));
    if (!snapshot.exists()) throw new Error('The saved Mention response could not be read. Retry the original incident.');
    return {...snapshot.data(), id: snapshot.id};
  }

  /**
   * Evaluate a single mention rule
   * @param {Object} rule - The rule to evaluate
   * @param {Object} mention - The mention to check
   * @param {Object} contract - Optional active contract
   * @param {Object} studentData - Pre-fetched student data
   * @returns {Promise<Object|null>} Created consequence when the rule triggers
   */
  async evaluateMentionRule(rule, mention, contract = null, studentData, phasePolicy) {
    try {
      phasePolicy ??= await this.ruleService.getPhasePolicy?.(mention.schoolId);
      if (!phaseRuleAllowsRecord(phasePolicy, rule, mention)) return null;
      if (phasePolicy?.enabled && this.usesTrustedMentions &&
          Object.prototype.hasOwnProperty.call(mention.mentionRuleResults || {}, rule.id)) {
        const saved = await this.recoverSavedResult(mention, rule.id);
        if (saved) {
          await this.consequenceService.resumeAccountabilityConsequence(saved);
          if (contract && this.contractService) {
            await this.contractService.applyContractModification(saved, contract);
          }
          applyMentionConsumptionToMemory(studentData?.mentions, saved);
          return saved;
        }
      }
      const eligibleHistory = record => phaseRuleAllowsRecord(phasePolicy, rule, record, {historical: true});
      disciplineLogger.info(`Checking mention rule: "${rule.title}"`, {
        scope: rule.mentionScope,
        threshold: rule.count,
        mentionTypeFilter: rule.mentionTypes || []
      });

      const dateRange = await getDateRangeForTimeframe(
          rule.timeframe, rule.timeCount || 1, mention.schoolId,
          getMentionRuleDate(mention), RULE_TIME_ZONE,
      );

      // Check history filter
      if (rule.historyFilter) {
        const passesHistoryFilter = await this.checkHistoryFilter(rule.historyFilter, mention.studentId, mention.schoolId, dateRange, eligibleHistory);
        if (!passesHistoryFilter) {
          disciplineLogger.debug('Rule failed history filter check');
          return;
        }
      }

      // Get mentions within timeframe based on scope
      const scopedMentions = await this.getMentionsForMentionScope(
        mention,
        rule.mentionScope,
        rule.timeCount,
        rule.timeframe,
        rule.mentionTypes, // Pass the mention types filter
        studentData, // Pass pre-fetched data
        dateRange
      );

      const mentions = scopedMentions.filter(eligibleHistory);

      disciplineLogger.info(`Evaluating rule: ${mentions.length}/${rule.count} mentions`);

      // Check if threshold is met
      if (mentions.length >= rule.count) {
        // Only use the first 'rule.count' mentions for triggering
        const triggeringMentions = mentions.slice(0, rule.count);

        disciplineLogger.critical(`MENTION RULE TRIGGERED: "${rule.title}"`, {
          studentId: mention.studentId,
          consequence: rule.consequenceType,
          count: mentions.length,
          usingCount: triggeringMentions.length
        });

        // Create consequence - only pass the mentions that trigger the rule
        const consequence = await this.createMentionRuleConsequence(rule, mention, triggeringMentions);

        // Let contract modify the outcome if present
        if (contract && this.contractService) {
          await this.contractService.applyContractModification(consequence, contract);
        }

        // Mark ONLY the triggering mentions as actioned, not all of them
        if (consequence.mentionPersistenceVersion === 1) {
          applyMentionConsumptionToMemory(studentData?.mentions, consequence);
        } else {
          await this.markMentionsAsActioned(triggeringMentions, rule.id);
        }
        return consequence;
      }
      return null;
    } catch (error) {
      if (isSchoolRulePeriodError(error) || isPhaseExecutionError(error) ||
          error.thresholdProcessingFailed) throw error;
      if (phasePolicy?.enabled) throw phaseExecutionError(error.message);
      console.error('Error evaluating mention rule:', error);
      return null;
    }
  }

  /**
   * Check if mention passes history filter
   * @param {string} historyFilter - Filter type
   * @param {string} studentId - Student ID
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<boolean>} True if passes filter
   */
  async checkHistoryFilter(historyFilter, studentId, schoolId, dateRange = null, eligibleHistory = () => true) {
    if (!schoolId) {
      disciplineLogger.critical('❌ checkHistoryFilter: schoolId is missing', { studentId });
      return true; // Allow rule to pass if we can't check history
    }

    const history = await this.mentionService.getMentionsByStudent(studentId, { schoolId });
    const studentMentionHistory = history.filter(record =>
      (!dateRange || ruleRecordInRange(record, dateRange)) && eligibleHistory(record));

    // Count reads for history filter check (legacy counter)
    countReads.increment('MentionRule.checkHistoryFilter', studentMentionHistory.length, {
      studentId,
      historyFilter,
      totalMentions: studentMentionHistory.length
    });

    // Track reads for ProcessingGuard
    processingGuard.trackReads('MentionRuleService.checkHistory', studentMentionHistory.length, {
      studentId,
      historyFilter
    });

    if (historyFilter === 'first-offense' && studentMentionHistory.length > 1) {
      return false;
    }
    if (historyFilter === '2-plus' && studentMentionHistory.length < 2) {
      return false;
    }
    if (historyFilter === '5-plus' && studentMentionHistory.length < 5) {
      return false;
    }

    return true;
  }

  /**
   * Get mentions matching the rule's scope criteria
   * @param {Object} mention - Current mention
   * @param {string} scope - Scope type
   * @param {number} timeCount - Time count
   * @param {string} timeframe - Timeframe
   * @param {Array} mentionTypes - Optional array of mention type values to filter by
   * @param {Object} studentData - Pre-fetched student data
   * @returns {Promise<Array>} Matching mentions
   */
  async getMentionsForMentionScope(mention, scope, timeCount, timeframe, mentionTypes = [], studentData = null, resolvedRange = null) {
    try {
      // Anchor the configured window to the incident. Delayed capture remains
      // rule-eligible, but only alongside events in its true incident window.
      const dateRange = resolvedRange || await getDateRangeForTimeframe(
        timeframe,
        timeCount,
        mention.schoolId,
        getMentionRuleDate(mention),
        RULE_TIME_ZONE
      );

      let mentions;

      if (studentData && studentData.mentions) {
        // Use pre-fetched data
        mentions = studentData.mentions.filter(m =>
          m.studentId === mention.studentId &&
          !m.actioned &&
          getMentionRuleDate(m) >= dateRange.start
        );

        disciplineLogger.debug('Using pre-fetched mentions data', {
          totalMentions: studentData.mentions.length,
          filteredMentions: mentions.length
        });
      } else {
        // Fallback to query (for backward compatibility)
        disciplineLogger.warn('No pre-fetched data for mentions, falling back to query');

        // CRITICAL: Validate schoolId for hierarchical query
        if (!mention.schoolId) {
          disciplineLogger.critical('❌ Cannot query mentions: mention.schoolId is missing', {
            mentionId: mention.id,
            studentId: mention.studentId
          });
          return []; // Return empty array to avoid crash
        }

        // Use hierarchical path for query
        const q = query(
          collection(db, 'schools', mention.schoolId, 'mentions'),
          where('studentId', '==', mention.studentId),
          where('actioned', '==', false)
        );

        const querySnapshot = await getDocs(q);
        mentions = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      }

      // Filter by date range, scope, and mention types
      mentions = mentions.filter(m => {
        const ruleDate = getMentionRuleDate(m);
        if (ruleDate < dateRange.start || ruleDate > dateRange.end) return false;

        // Filter by mention type ONLY if rule specifies types
        if (mentionTypes && mentionTypes.length > 0) {
          const mentionType = m.mentionType || 'mention'; // Default to 'mention' if undefined

          // Rule specifies types - check if this mention's type is in the list
          if (!mentionTypes.includes(mentionType)) {
            console.log('❌ MENTION FILTERED OUT - type not in allowed list', {
              mentionType: mentionType,
              allowedTypes: mentionTypes
            });
            return false;
          }
          console.log('✅ MENTION PASSED FILTER - type matches', { mentionType });
        }
        // If rule has NO type filter, it accepts ALL mentions regardless of type

        // Apply scope filter
        return this.matchesScope(m, mention, scope);
      });

      // Sort by date (oldest first) so we action them in order
      mentions.sort((a, b) => {
        return getMentionRuleDate(a) - getMentionRuleDate(b);
      });

      return mentions;
    } catch (error) {
      if (isSchoolRulePeriodError(error) || isPhaseExecutionError(error) ||
          error.thresholdProcessingFailed) throw error;
      console.error('Error getting mentions for scope:', error);
      return [];
    }
  }

  /**
   * Check if a mention matches the scope criteria
   * @param {Object} m - Mention to check
   * @param {Object} mention - Current mention
   * @param {string} scope - Scope type
   * @returns {boolean} True if matches
   */
  matchesScope(m, mention, scope) {
    switch (scope) {
      case 'same-offense': {
        // Exact same offense - check both offence (primary) and reason (fallback) fields
        const currentOffence = mention.offence || mention.reason;
        const comparedOffence = m.offence || m.reason;
        return currentOffence === comparedOffence;
      }

      case 'same-category': {
        // Same offense category - handle 'uncategorized' as a valid category
        const currentCategory = mention.offenseCategory || 'uncategorized';
        const mentionCategory = m.offenseCategory || 'uncategorized';
        return currentCategory === mentionCategory;
      }

      case 'same-lesson': {
        // Same class within 2 hours
        if (m.classId !== mention.classId) return false;
        const currentTime = mention.issuedAt?.toDate?.() || new Date(mention.issuedAt);
        const mentionTime = m.issuedAt?.toDate?.() || new Date(m.issuedAt);
        const timeDiff = Math.abs(currentTime - mentionTime);
        return timeDiff <= 2 * 60 * 60 * 1000; // 2 hours
      }

      case 'any':
        // All mentions count
        return true;

      default:
        console.warn(`Unknown scope: ${scope}`);
        return false;
    }
  }

  /**
   * Create consequence for triggered mention rule
   * @param {Object} rule - The triggered rule
   * @param {Object} mention - The current mention
   * @param {Array} triggeringMentions - Mentions that triggered the rule
   * @returns {Promise<Object>} Created consequence
   */
  async createMentionRuleConsequence(rule, mention, triggeringMentions) {
    try {
      // Delegate to ConsequenceService for creation
      const result = await this.consequenceService.createMentionRuleConsequence(rule, mention, triggeringMentions);

      if (result.mentionPersistenceVersion === 1) return result;

      // Update mentions to mark them as having triggered a consequence
      for (const mentionItem of triggeringMentions) {
        await this.mentionService.updateMention(mentionItem.id, {
          schoolId: mentionItem.schoolId,
          triggeredConsequences: [...(mentionItem.triggeredConsequences || []), result.id]
        });
      }

      return result;
    } catch (error) {
      if (isSchoolRulePeriodError(error) || isPhaseExecutionError(error) ||
          error.thresholdProcessingFailed) throw error;
      console.error('Error creating mention rule consequence:', error);
      throw error;
    }
  }

  /**
   * Mark mentions as actioned
   * @param {Array} mentions - Mentions to mark
   * @param {string} ruleId - Rule ID that actioned them
   * @returns {Promise<void>}
   */
  async markMentionsAsActioned(mentions, ruleId) {
    const now = Timestamp.now();
    for (const mentionItem of mentions) {
      if (!mentionItem.schoolId) {
        console.error('Cannot update mention: missing schoolId', { mentionId: mentionItem.id });
        continue;
      }

      // Update Firestore using hierarchical path
      await updateDoc(doc(db, 'schools', mentionItem.schoolId, 'mentions', mentionItem.id), {
        actioned: true,
        actionedBy: arrayUnion(ruleId),
        actionedAt: now
      });

      // CRITICAL: Also update the in-memory object so other rules see it as consumed
      mentionItem.actioned = true;
      mentionItem.actionedBy = [...(mentionItem.actionedBy || []), ruleId];
      mentionItem.actionedAt = now;
    }
  }
}
